import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { IdCard, CheckCircle2, FileText } from "lucide-react";

const DOC_TYPES = ["Carta d'identità", "Patente", "Passaporto"];
const GENERI = ["Maschio", "Femmina", "Altro", "Preferisco non specificarlo"];

export default function Tesseramento() {
  const { content } = useOutletContext() || {};
  const c = content || {};
  const [assoc, setAssoc] = useState("DAMACO_APS");
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [form, setForm] = useState({
    nome:"", cognome:"", genere:"", data_nascita:"", luogo_nascita:"",
    tipo_documento:"Carta d'identità", numero_documento:"",
    luogo_residenza:"", email:"", telefono:"",
  });
  const [privacy, setPrivacy] = useState(false);
  const [step, setStep] = useState("form"); // form | payment | done
  const [socioId, setSocioId] = useState(null);
  const [busy, setBusy] = useState(false);

  const update = (k, v) => setForm(s => ({ ...s, [k]: v }));

  const submitForm = async (inLoco = false) => {
    if (!privacy) return toast.error("Devi accettare l'informativa privacy");
    for (const k of Object.keys(form)) if (!form[k]) return toast.error("Compila tutti i campi");
    setBusy(true);
    try {
      const { data } = await api.post("/soci", {
        association: assoc, ...form, privacy_accepted: true, in_loco: inLoco,
      });
      setSocioId(data.id);
      if (inLoco) { setStep("done"); toast.success("Richiesta registrata! Paga in loco 6€"); }
      else { setStep("payment"); }
    } catch (e) { toast.error("Errore nella registrazione"); }
    setBusy(false);
  };

  const payNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: "membership_flow", quantity: 1,
        origin_url: window.location.origin,
        metadata: { socio_id: socioId, association: assoc, type: "membership" },
      });
      window.location.href = data.checkout_url;
    } catch { toast.error("Errore checkout"); setBusy(false); }
  };

  if (step === "done") {
    return (
      <section className="mx-auto max-w-3xl px-4 py-24 text-center">
        <CheckCircle2 size={64} className="mx-auto text-[#FFFFFF]"/>
        <h1 className="mt-6 font-display text-5xl uppercase" data-testid="tesseramento-done">Richiesta ricevuta</h1>
        <p className="mt-4 text-white">Il tuo tesseramento a <strong>{assoc.replace("_"," ")}</strong> è stato registrato.<br/>
          Pagherai la quota di 6€ in loco. Ti verrà consegnata la tessera fisica al primo evento.</p>
        <a href="/" className="brutal-btn brutal-btn-outline mt-8">Torna alla home</a>
      </section>
    );
  }

  if (step === "payment") {
    return (
      <section className="mx-auto max-w-3xl px-4 py-24 text-center">
        <div className="tag-pill mb-4 mx-auto"><IdCard size={12}/> Pagamento quota</div>
        <h1 className="font-display text-5xl uppercase">Ultimo passo: 6€</h1>
        <p className="mt-4 text-white">Paga online la quota associativa {assoc.replace("_"," ")} per completare il tesseramento.</p>
        <button data-testid="pay-membership" onClick={payNow} disabled={busy} className="brutal-btn brutal-btn-primary mt-8">
          {busy ? "Attendere..." : "Paga 6€ con Stripe"}
        </button>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-14">
      <div className="mb-8">
        <div className="tag-pill mb-3"><IdCard size={12}/> Tesseramento APS</div>
        <h1 className="font-display text-5xl uppercase text-white" data-testid="tesseramento-heading">{c.tesseramento_title || "Diventa socio"}</h1>
        <p className="mt-3 text-white/70 whitespace-pre-line">{c.tesseramento_subtitle || ""}</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mb-8">
        {[
          { k:"DAMACO_APS", label:"DAMACO APS", color:"#FFFFFF" },
          { k:"ROCCA_APS", label:"ROCCA APS", color:"#FFFFFF" },
        ].map(a => (
          <button key={a.k} onClick={() => setAssoc(a.k)} data-testid={`assoc-${a.k}`}
            className={`card p-5 text-left transition-all ${assoc===a.k ? "border-[#FFFFFF]" : ""}`}
            style={assoc===a.k ? { borderColor: a.color } : {}}>
            <div className="text-xs font-mono uppercase tracking-widest" style={{color: a.color}}>Associazione</div>
            <div className="font-display text-3xl uppercase mt-1">{a.label}</div>
            {assoc===a.k && <div className="mt-2 text-xs text-white/70">Selezionata</div>}
          </button>
        ))}
      </div>

      <div className="card p-6 grid md:grid-cols-2 gap-4">
        <Field label="Nome" tid="f-nome" val={form.nome} onChange={v => update("nome", v)}/>
        <Field label="Cognome" tid="f-cognome" val={form.cognome} onChange={v => update("cognome", v)}/>
        <div>
          <label className="block text-xs font-mono uppercase tracking-widest text-[#FFFFFF] mb-1">Genere</label>
          <select data-testid="f-genere" value={form.genere} onChange={e => update("genere", e.target.value)}
            className="w-full bg-[#000000] border border-[#FFFFFF] px-3 py-3 rounded text-white">
            <option value="">Seleziona</option>
            {GENERI.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
        <Field label="Data di nascita" tid="f-nascita" type="date" val={form.data_nascita} onChange={v => update("data_nascita", v)}/>
        <Field label="Luogo di nascita" tid="f-luogo-nascita" val={form.luogo_nascita} onChange={v => update("luogo_nascita", v)}/>
        <div>
          <label className="block text-xs font-mono uppercase tracking-widest text-[#FFFFFF] mb-1">Tipo documento</label>
          <select data-testid="f-tipo-doc" value={form.tipo_documento} onChange={e => update("tipo_documento", e.target.value)}
            className="w-full bg-[#000000] border border-[#FFFFFF] px-3 py-3 rounded text-white">
            {DOC_TYPES.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <Field label="Numero documento" tid="f-num-doc" val={form.numero_documento} onChange={v => update("numero_documento", v)}/>
        <Field label="Luogo di residenza" tid="f-residenza" val={form.luogo_residenza} onChange={v => update("luogo_residenza", v)}/>
        <Field label="Email" tid="f-email" type="email" val={form.email} onChange={v => update("email", v)}/>
        <Field label="Telefono" tid="f-telefono" val={form.telefono} onChange={v => update("telefono", v)}/>
      </div>

      <label className="mt-6 flex items-start gap-3 cursor-pointer">
        <input data-testid="f-privacy" type="checkbox" checked={privacy} onChange={e => setPrivacy(e.target.checked)}
          className="mt-1 w-4 h-4 accent-[#FFFFFF]"/>
        <span className="text-sm text-white">
          Ho letto e accetto l'
          {c.privacy_pdf_url ? (
            <a href={c.privacy_pdf_url} target="_blank" rel="noopener noreferrer" className="text-white underline">Informativa Privacy (PDF)</a>
          ) : (
            <button type="button" onClick={e => {e.preventDefault(); setShowPrivacy(true);}} className="text-white underline">Informativa Privacy</button>
          )}
          {' '}ai sensi del Regolamento UE 2016/679 (GDPR).
        </span>
      </label>

      {showPrivacy && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90" onClick={() => setShowPrivacy(false)}>
          <div className="card p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4">
              <FileText size={16}/><h3 className="font-display text-2xl uppercase text-white">Informativa Privacy</h3>
            </div>
            <p className="text-sm text-white/80 whitespace-pre-line">{c.privacy_text || "Testo dell'informativa non ancora inserito."}</p>
            <button onClick={() => setShowPrivacy(false)} className="brutal-btn brutal-btn-outline mt-6 text-xs">Chiudi</button>
          </div>
        </div>
      )}

      <div className="mt-8 flex flex-col sm:flex-row gap-3">
        <button data-testid="submit-online" onClick={() => submitForm(false)} disabled={busy} className="brutal-btn brutal-btn-primary">
          Continua e paga 6€ online
        </button>
        <button data-testid="submit-in-loco" onClick={() => submitForm(true)} disabled={busy} className="brutal-btn brutal-btn-outline">
          Pago in loco / contanti
        </button>
      </div>
    </section>
  );
}

function Field({ label, tid, val, onChange, type="text" }) {
  return (
    <div>
      <label className="block text-xs font-mono uppercase tracking-widest text-[#FFFFFF] mb-1">{label}</label>
      <input data-testid={tid} type={type} value={val} onChange={e => onChange(e.target.value)}
        className="w-full bg-[#000000] border border-[#FFFFFF] px-3 py-3 rounded text-white"/>
    </div>
  );
}
